import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, useMediaQuery, CircularProgress } from '@mui/material';
import { FaArrowLeft, FaShareAlt, FaCheck, FaTimes } from 'react-icons/fa';

export default function QuestionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);

  useEffect(() => {
    fetchQuestion();
  }, [id]);

  const fetchQuestion = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/practice-questions/${id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Question not found');
      }
      
      const data = await response.json();
      setQuestion(data.question);
      setRequiresAuth(data.requiresAuth || false);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAnswerSelect = (answer) => {
    if (showFeedback) return;
    setSelectedAnswer(answer);
    setShowFeedback(true);
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: 'سؤال من ياعيل ماستر',
        text: 'شاهد هذا السؤال من منصة ياعيل ماستر',
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('تم نسخ الرابط!');
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        direction: 'rtl'
      }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        direction: 'rtl', 
        padding: isMobile ? '1rem' : '2rem', 
        maxWidth: 800, 
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <h2 style={{ color: '#ef4444' }}>❌ {error}</h2>
            <Link to="/practice" style={{ 
              color: '#667eea', 
              textDecoration: 'none',
              fontWeight: 'bold',
              fontSize: 18
            }}>
              العودة إلى التمارين
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!question) {
    return null;
  }

  const renderQuestion = () => {
    if (question.type === 'sentence' || question.type === 'vocab' || question.type === 'rephrase') {
      return (
        <>
          <div style={{
            fontSize: isMobile ? 20 : 24,
            fontWeight: 'bold',
            marginBottom: 24,
            textAlign: 'center',
            color: '#1f2937',
            lineHeight: 1.6
          }}>
            {question.question || question.sentence || question.text || ''}
          </div>

          {question.choices && question.choices.length > 0 ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {question.choices && question.choices.map((choice, idx) => {
                  const isCorrect = choice === question.correct;
                  const isSelected = choice === selectedAnswer;
                  
                  let backgroundColor = '#f3f4f6';
                  let borderColor = '#d1d5db';
                  let icon = null;

                  if (showFeedback && !requiresAuth) {
                    if (isCorrect) {
                      backgroundColor = '#d1fae5';
                      borderColor = '#10b981';
                      icon = <FaCheck style={{ color: '#10b981' }} />;
                    } else if (isSelected && !isCorrect) {
                      backgroundColor = '#fee2e2';
                      borderColor = '#ef4444';
                      icon = <FaTimes style={{ color: '#ef4444' }} />;
                    }
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => !requiresAuth && handleAnswerSelect(choice)}
                      disabled={showFeedback || requiresAuth}
                      style={{
                        padding: isMobile ? '14px 18px' : '16px 24px',
                        background: backgroundColor,
                        border: `2px solid ${borderColor}`,
                        borderRadius: 12,
                        fontSize: isMobile ? 16 : 18,
                        cursor: (showFeedback || requiresAuth) ? 'default' : 'pointer',
                        textAlign: 'right',
                        transition: 'all 0.2s',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: isCorrect && showFeedback && !requiresAuth ? 'bold' : 'normal',
                        opacity: requiresAuth ? 0.7 : 1
                      }}
                    >
                      <span>{choice}</span>
                      {icon}
                    </button>
                  );
                })}
              </div>
              
              {requiresAuth && (
                <div style={{
                  padding: 24,
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: 12,
                  textAlign: 'center',
                  marginTop: 16,
                  border: '2px solid #f59e0b'
                }}>
                  <p style={{ fontSize: 18, marginBottom: 16, fontWeight: 'bold', color: '#92400e' }}>
                    🔒 سجل دخولك لرؤية الإجابة الصحيحة والشرح
                  </p>
                  <Link 
                    to={`/login?returnTo=${encodeURIComponent(window.location.pathname)}`}
                    style={{
                      background: '#667eea',
                      color: '#fff',
                      padding: '12px 24px',
                      borderRadius: 8,
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      display: 'inline-block'
                    }}
                  >
                    تسجيل الدخول
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div style={{
              padding: 24,
              background: '#f3f4f6',
              borderRadius: 12,
              textAlign: 'center',
              color: '#6b7280'
            }}>
              لا توجد خيارات متاحة لهذا السؤال
            </div>
          )}

          {showFeedback && question.explanation && !requiresAuth && (
            <div style={{
              marginTop: 24,
              padding: 20,
              background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
              borderRadius: 12,
              border: '2px solid #818cf8'
            }}>
              <h4 style={{ marginTop: 0, color: '#4338ca' }}>💡 الشرح:</h4>
              <p style={{ margin: 0, lineHeight: 1.6, fontSize: 16 }}>
                {question.explanation}
              </p>
            </div>
          )}
        </>
      );
    }

    // Add more question types as needed
    return <div>نوع السؤال: {question.type}</div>;
  };

  return (
    <div style={{ 
      direction: 'rtl', 
      padding: isMobile ? '1rem' : '2rem', 
      maxWidth: 900, 
      margin: '0 auto'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 24
      }}>
        <button
          onClick={() => navigate('/practice')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            padding: '10px 16px',
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 'bold',
            color: '#374151'
          }}
        >
          <FaArrowLeft />
          العودة
        </button>

        <button
          onClick={handleShare}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: 8,
            padding: '10px 16px',
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 'bold',
            color: '#fff'
          }}
        >
          <FaShareAlt />
          مشاركة
        </button>
      </div>

      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)'
      }}>
        <CardContent sx={{ p: isMobile ? 3 : 4 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            flexWrap: 'wrap',
            gap: 12
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: 20,
              fontSize: 14,
              fontWeight: 'bold'
            }}>
              {question.type === 'sentence' ? 'إكمال الجمل' : 
               question.type === 'vocab' ? 'مفردات' :
               question.type === 'rephrase' ? 'إعادة الصياغة' :
               question.type === 'reading' ? 'فهم المقروء' :
               question.type === 'essay' ? 'كتابة مقال' :
               question.type}
            </span>

            <span style={{
              background: question.level === 'easy' ? '#d1fae5' : 
                         question.level === 'middle' ? '#fef3c7' : '#fee2e2',
              color: question.level === 'easy' ? '#065f46' : 
                     question.level === 'middle' ? '#92400e' : '#991b1b',
              padding: '6px 14px',
              borderRadius: 20,
              fontSize: 14,
              fontWeight: 'bold'
            }}>
              {question.level === 'easy' ? 'سهل' : 
               question.level === 'middle' ? 'متوسط' : 'صعب'}
            </span>
          </div>

          {renderQuestion()}
        </CardContent>
      </Card>
    </div>
  );
}

